import React, { useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Box, Text, Button, Group, Badge, Select, MultiSelect, Loader, Center, Tabs, Chip, Grid } from '@mantine/core'
import { format, formatDistanceToNow } from 'date-fns'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { ReloaderRecipeDto } from '../../app/models/reloaderRecipe'
import { AmmoScoreBadge } from '../../app/common/ui'
import { CartridgeSpecDto } from '../../app/models/ammo'

const INTENDED_USES = [
  { value: 'target', label: 'Target' },
  { value: 'hunting', label: 'Hunting' },
  { value: 'defense', label: 'Defense' },
  { value: 'plinking', label: 'Plinking' }
]

export default function ReloaderForum () {
  const history = useHistory()
  const location = useLocation()
  const { userStore } = useStore()
  const [recipes, setRecipes] = useState<ReloaderRecipeDto[]>([])
  const [cartridgeSpecs, setCartridgeSpecs] = useState<CartridgeSpecDto[]>([])
  const [loading, setLoading] = useState(false)
  const [cartridgeId, setCartridgeId] = useState<string | null>(() => {
    const params = new URLSearchParams(location.search)
    return params.get('cartridgeSpecId')
  })
  const [powderType, setPowderType] = useState('')
  const [intendedUse, setIntendedUse] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState('validated')
  const [showFlagged, setShowFlagged] = useState(false)

  const loadRecipes = async () => {
    setLoading(true)
    try {
      const result = await agent.ReloaderRecipes.list({
        cartridgeSpecId: cartridgeId || undefined,
        powderType: powderType || undefined,
        intendedUse: intendedUse || undefined,
        isFlagged: showFlagged ? true : undefined,
        pageSize: 50
      })
      setRecipes(result.items)
    } catch (error) {
      toast.error('Failed to load recipes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    agent.CartridgeSpecs.list({ pageSize: 200 })
      .then((r) => setCartridgeSpecs(r.items))
      .catch(() => {})
  }, [])

  useEffect(() => {
    loadRecipes()
  }, [cartridgeId, powderType, intendedUse, sortBy, showFlagged])

  const canPost = userStore.user && userStore.user.ammoScore >= 50

  return (
    <Box sx={{ fontFamily: 'DM Sans, sans-serif', maxWidth: 900, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <Box mb="lg">
        <Text sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 600, marginBottom: 4 }}>
          RELOADER FORUM
        </Text>
        <Text size="sm" color="dimmed">Share and test reloading recipes with the community</Text>
      </Box>

      {/* Post Recipe Button */}
      <Box mb="lg">
        <Button
          component={Link}
          to="/ammo/reloading/new"
          color="ember"
          disabled={!canPost}
          title={!canPost ? `Requires 50 AmmoScore (yours: ${userStore.user?.ammoScore ?? 0})` : ''}
        >
          + Post a Recipe
        </Button>
      </Box>

      {/* Filters */}
      <Box mb="lg" sx={{ background: 'var(--gs-surface)', padding: 16, borderRadius: 6 }}>
        <Grid gutter="sm" mb="sm">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Cartridge"
              placeholder="All cartridges"
              searchable
              clearable
              value={cartridgeId}
              onChange={setCartridgeId}
              data={[
                { value: '', label: 'All Calibers' },
                ...cartridgeSpecs.map((s) => ({ value: s.id, label: s.name }))
              ]}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Powder Type"
              placeholder="All powders"
              searchable
              value={powderType}
              onChange={setPowderType}
              data={[]}
            />
          </Grid.Col>
        </Grid>

        <Group mb="sm">
          <Text size="sm" weight={500}>Intended Use:</Text>
          {['target', 'hunting', 'defense', 'plinking'].map((use) => (
            <Chip
              key={use}
              variant={intendedUse === use ? 'filled' : 'light'}
              onClick={() => setIntendedUse(intendedUse === use ? null : use)}
            >
              {INTENDED_USES.find(u => u.value === use)?.label}
            </Chip>
          ))}
        </Group>

        <Group>
          <Select
            label="Sort"
            value={sortBy}
            onChange={(v) => setSortBy(v || 'validated')}
            data={[
              { value: 'validated', label: 'Most Validated' },
              { value: 'newest', label: 'Newest' }
            ]}
            w={200}
          />
          {userStore.user?.isModerator && (
            <Chip
              variant={showFlagged ? 'filled' : 'light'}
              onClick={() => setShowFlagged(!showFlagged)}
            >
              Show Flagged
            </Chip>
          )}
        </Group>
      </Box>

      {/* Recipe Cards */}
      {loading
        ? (
        <Center py="xl">
          <Loader />
        </Center>
          )
        : recipes.length === 0
          ? (
        <Box sx={{ textAlign: 'center', padding: 32 }}>
          <Text color="dimmed">No recipes found. Be the first to post one!</Text>
        </Box>
            )
          : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {recipes.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} onClick={() => history.push(`/ammo/reloading/${recipe.id}`)} />
          ))}
        </Box>
            )}
    </Box>
  )
}

function RecipeCard ({ recipe, onClick }: { recipe: ReloaderRecipeDto; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        padding: 16,
        border: '1px solid var(--gs-border)',
        borderRadius: 6,
        background: 'var(--gs-card)',
        cursor: 'pointer',
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: 'var(--gs-accent)' }
      }}
    >
      {recipe.isFlagged && (
        <Box sx={{ background: '#ffa94d', padding: 8, borderRadius: 4, marginBottom: 12 }}>
          <Text size="xs" weight={600} sx={{ color: '#333' }}>
            ⚠️ Safety flagged — review before use
          </Text>
        </Box>
      )}

      <Group position="apart" align="flex-start" mb="xs">
        <Box sx={{ flex: 1 }}>
          <Text component={Link} to={`/ammo/specs/${recipe.cartridgeSpecId}`} weight={600} sx={{ color: 'var(--gs-text)' }}>
            {recipe.cartridgeSpecName}
          </Text>
          <Text size="sm" sx={{ fontFamily: 'JetBrains Mono, monospace', marginTop: 4, marginBottom: 4 }}>
            <strong>{recipe.powderBrand} {recipe.powderType}</strong> {recipe.chargeWeightGrMin}-{recipe.chargeWeightGrMax}gr
          </Text>
          <Text size="sm" color="dimmed">
            {recipe.bulletWeightGr}gr {recipe.bulletType} — {recipe.bulletBrand}
          </Text>
          <Text size="xs" color="dimmed">
            Primer: {recipe.primerBrand} {recipe.primerType} | OAL: {recipe.oalIn}"
          </Text>
          {recipe.estimatedVelocityFps && (
            <Text size="xs" sx={{ fontFamily: 'JetBrains Mono, monospace', marginTop: 4 }}>
              ~{recipe.estimatedVelocityFps} fps
            </Text>
          )}
        </Box>
        <Group position="right" sx={{ flexShrink: 0 }}>
          <Badge size="lg" color="green" variant="filled">
            {recipe.validationCount} Validated
          </Badge>
        </Group>
      </Group>

      <Group spacing="xs" mt="sm" position="apart">
        <Group spacing="xs">
          <Badge size="sm" variant="outline">
            {recipe.intendedUse}
          </Badge>
        </Group>
        <Group spacing="xs">
          <Text component={Link} to={`/profiles/${recipe.authorUsername}`} size="xs" weight={500} sx={{ color: 'var(--gs-accent)' }}>
            {recipe.authorDisplayName || recipe.authorUsername}
          </Text>
          <AmmoScoreBadge tier={recipe.authorAmmoTier} score={recipe.authorAmmoScore} size="xs" />
          <Text size="xs" color="dimmed">
            {formatDistanceToNow(new Date(recipe.createdAt), { addSuffix: true })}
          </Text>
        </Group>
      </Group>
    </Box>
  )
}
